import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DialogProps } from '@economic/taco';
import { useCurrentAgreement, useCurrentUser, type User, useRouteAuthorisation } from '@economic/react-app';
import PaymentCardsOnboardingDialogProvider from './Provider';
import { defaultPaymentCardRoute, paymentCardsUrlBuilder } from '../windowUtils';

interface PaymentCardsOnboardingDialogProps extends Partial<DialogProps> {
    inMarket?: boolean;
}

const getWalletUrl = (
    user: User,
    agreementNumber: number,
    hasAccessToAccounting: boolean,
    hasAccessToExpenses: boolean
): string => {
    const paths = {
        accounting: 'wallet/overview',
        expenses: 'wallet/overview',
    };

    const currentPath = defaultPaymentCardRoute(user, agreementNumber, hasAccessToAccounting, hasAccessToExpenses);
    if (currentPath && paths[currentPath]) {
        return paymentCardsUrlBuilder(paths[currentPath], { parentPage: currentPath });
    }
    return '';
};

const PaymentCardsOnboardingDialog = ({ inMarket, ...dialogProps }: PaymentCardsOnboardingDialogProps) => {
    const agreement = useCurrentAgreement();
    const user = useCurrentUser();
    const location = useLocation();
    const navigate = useNavigate();
    const [hasAccessToAccounting] = useRouteAuthorisation('/accounting/payment-cards');
    const [hasAccessToExpenses] = useRouteAuthorisation('/expenses/payment-cards');
    if (!agreement.features.paymentCards) {
        return null;
    }

    const onCloseDialog = () => {
        if (!inMarket) {
            return;
        }
        navigate(location.pathname);
    };

    const walletUrl = getWalletUrl(user, agreement.number, hasAccessToAccounting, hasAccessToExpenses);

    return (
        <PaymentCardsOnboardingDialogProvider
            {...dialogProps}
            open={dialogProps.open ?? dialogProps.defaultOpen}
            agreementNumber={agreement.number}
            walletUrl={walletUrl}
            onCloseDialog={onCloseDialog}
        />
    );
};

export default PaymentCardsOnboardingDialog;
